import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  QrCode, 
  Download, 
  Share2, 
  Copy,
  Building2,
  Award,
  MapPin,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const SocialActorQRCode = ({ actor, isOpen, onClose, publicUrl }) => {
  const qrRef = useRef(null);
  
  if (!actor) return null;

  // Generate the public profile URL
  const profileUrl = publicUrl || `${window.location.origin}/public-registry?view=${actor.id}`;
  
  // Certification label
  const getCertificationLabel = (level) => {
    const labels = {
      'level_3_excellence': '🏆 Level A - Excellence',
      'level_2_commitment': '⭐ Level B - Commitment',
      'level_1_basic': '✓ Level C - Basic',
      'none': 'Not Certified'
    };
    return labels[level] || labels['none'];
  };

  // Download QR code as PNG
  const handleDownload = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast.error('Could not generate QR code');
      return;
    }

    // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    // Set canvas size (larger for better quality)
    canvas.width = 400;
    canvas.height = 500;
    
    img.onload = () => {
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(img, 50, 30, 300, 300);
      
      // Add organization name
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(actor.organization_name, canvas.width / 2, 370);
      
      // Add certification level
      ctx.font = '14px Arial';
      ctx.fillStyle = '#6b7280';
      ctx.fillText(getCertificationLabel(actor.certification_level), canvas.width / 2, 395);
      
      // Add "Scan to view profile"
      ctx.font = '12px Arial';
      ctx.fillStyle = '#9ca3af';
      ctx.fillText('Scan to view profile', canvas.width / 2, 420);
      
      // Add footer
      ctx.font = '10px Arial';
      ctx.fillText('ISISP Social Actor Registry', canvas.width / 2, 470);
      
      // Download
      const link = document.createElement('a');
      link.download = `${actor.organization_name.replace(/\s+/g, '_')}_QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('QR code downloaded!');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Copy URL to clipboard
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success('URL copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  // Share (if available)
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: actor.organization_name,
          text: `Check out ${actor.organization_name} on ISISP Social Actor Registry`,
          url: profileUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      handleCopyUrl();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-blue-600" />
            Organization QR Code
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Organization Info */}
          <div className="text-center pb-4 border-b">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-lg">{actor.organization_name}</h3>
            </div>
            <p className="text-sm text-gray-600 capitalize mb-2">
              {actor.organization_type?.replace('_', ' ')}
            </p>
            <div className="flex items-center justify-center gap-2">
              <Badge className={`
                ${actor.certification_level === 'level_3_excellence' ? 'bg-amber-100 text-amber-800' :
                  actor.certification_level === 'level_2_commitment' ? 'bg-blue-100 text-blue-800' :
                  actor.certification_level === 'level_1_basic' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'}
              `}>
                <Award className="h-3 w-3 mr-1" />
                {getCertificationLabel(actor.certification_level)}
              </Badge>
            </div>
            {actor.city && (
              <div className="flex items-center justify-center gap-1 mt-2 text-sm text-gray-500">
                <MapPin className="h-3 w-3" />
                {actor.city}, {actor.country}
              </div>
            )}
          </div>
          
          {/* QR Code */}
          <div 
            ref={qrRef}
            className="flex justify-center p-6 bg-white rounded-lg border"
          >
            <QRCodeSVG
              value={profileUrl}
              size={200}
              level="H"
              includeMargin={true}
              imageSettings={{
                src: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%233b82f6'%3E%3Cpath d='M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5'/%3E%3C/svg%3E",
                x: undefined,
                y: undefined,
                height: 30,
                width: 30,
                excavate: true,
              }}
            />
          </div>
          
          {/* URL Display */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Profile URL:</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-white px-2 py-1 rounded border flex-1 truncate">
                {profileUrl}
              </code>
              <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          {/* Instructions */}
          <div className="text-center text-sm text-gray-500">
            <p>Scan this QR code to view the organization profile</p>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2">
            <Button 
              className="flex-1" 
              onClick={handleDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              Download PNG
            </Button>
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleShare}
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
          
          {/* Open in new tab */}
          <Button 
            variant="ghost" 
            className="w-full text-blue-600"
            onClick={() => window.open(profileUrl, '_blank')}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Profile in New Tab
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Inline QR code for cards (smaller version)
export const SocialActorQRCodeInline = ({ actor, size = 80 }) => {
  if (!actor) return null;
  
  const profileUrl = `${window.location.origin}/public-registry?view=${actor.id}`;
  
  return (
    <div className="flex flex-col items-center">
      <QRCodeSVG
        value={profileUrl}
        size={size}
        level="M"
        includeMargin={false}
      />
      <p className="text-xs text-gray-500 mt-1">Scan for profile</p>
    </div>
  );
};

export default SocialActorQRCode;
